﻿import $ from "jquery";

import { GuiBase } from "../GuiBase";
import { AppLog } from "../AppLog";
import { Chart, ChartConfiguration } from "../chart-setup";
import { IChartData } from "../Interface/IChartData";

export class GuiDashboard extends GuiBase {

    green5 = "#044135";

    constructor() {
        super("Dashboard");

        this.zeichneCharts($(document));
        this.ladeKarten();
    }

    /**
     * Holt den Inhalt aller nachzuladenden Karten (DEVCLR-327). Jede Karte lädt einzeln - eine
     * langsame oder fehlerhafte Karte hält weder die Seite noch die anderen Karten auf.
     * Das HTML kommt fertig vom Server, hier wird nichts zusammengebaut.
     */
    private ladeKarten() {
        $("div[data-karte-url]").each((_index, element) => {
            this.ladeKarte($(element));
        });
    }

    private ladeKarte(divKarte: JQuery<HTMLElement>) {
        const url = divKarte.data("karte-url") as string;
        if (url == undefined || url === "") {
            return;
        }

        AppLog.logInfo("GuiDashboard", `ladeKarte ${url}`);

        $.ajax({
            type: "GET",
            url: url,
            dataType: "html"
        }).done((html: string) => {
            divKarte.html(html).attr("aria-busy", "false");
            // Eine nachgeladene Karte kann eine Grafik enthalten - gezeichnet wird sie mit
            // demselben Code wie bei einer sofort gerenderten Karte.
            this.zeichneCharts(divKarte);
        }).fail(() => {
            AppLog.logError("GuiDashboard", `ladeKarte fehlgeschlagen ${url}`);
            divKarte.html('<p class="card-text text-secondary">Die Daten konnten nicht geladen werden.</p>').attr("aria-busy", "false");
        });
    }

    /**
     * Zeichnet jede Grafik innerhalb des übergebenen Bereichs. Daten und Beschriftung stehen als
     * data-Attribute am Canvas - die Karte bringt alles mit, was sie braucht, deshalb kommt hier
     * keine kartenspezifische Logik mehr dazu.
     */
    private zeichneCharts(bereich: JQuery<HTMLElement> | JQuery<Document>) {
        bereich.find("canvas[data-karte-chart]").each((_index, element) => {
            this.zeichneChart($(element));
        });
    }

    private zeichneChart(canvas: JQuery<HTMLElement>) {
        const daten = canvas.data("karte-chart-daten") as IChartData;
        if (daten == undefined || daten.values == undefined || daten.values.length === 0) {
            return;
        }

        // Einheit (z.B. "€") an y-Achse und Tooltip; Zahlen deutsch formatiert. Kleine Werte (z.B.
        // 0,4 t) mit einer Nachkommastelle, sonst stünde dort 0.
        const einheit = (canvas.data("karte-chart-einheit") as string) ?? "";
        const formatiere = (wert: number) => `${wert.toLocaleString("de-DE", { maximumFractionDigits: Math.abs(wert) < 10 ? 1 : 0 })}${einheit === "" ? "" : " " + einheit}`;

        const typ = canvas.data("karte-chart") as string;
        const config = typ === "doughnut"
            ? this.configDoughnut(canvas, daten, formatiere)
            : this.configVerlauf(canvas, daten, typ === "bar" ? "bar" : "line", formatiere);

        new Chart(canvas[0] as HTMLCanvasElement, config);
    }

    /**
     * Anteile als Doughnut (DEVCLR-365), z.B. die Abfallbilanz. Die Farben je Segment liefert der
     * Server (ClearoColors) - dieselben wie in der Auswertung. Ohne Achsen; die Legende steht
     * rechts, weil die Segmente sonst keine Beschriftung hätten.
     */
    private configDoughnut(canvas: JQuery<HTMLElement>, daten: IChartData, formatiere: (wert: number) => string): ChartConfiguration {
        // Eigene Legende als HTML neben dem Ring (DEVCLR-374) - dann zeichnet Chart.js keine.
        const legendeExtern = (canvas.data("karte-chart-legende") as string) === "extern";

        return {
            type: "doughnut",
            data: {
                labels: daten.labels,
                datasets: [{
                    label: canvas.data("karte-chart-reihe") as string,
                    data: daten.values,
                    backgroundColor: daten.colors != undefined && daten.colors.length > 0 ? daten.colors : this.green5
                }]
            },
            options: {
                plugins: {
                    legend: {
                        display: !legendeExtern,
                        position: "right",
                        labels: { boxWidth: 10, font: { size: 11 } }
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => `${context.label}: ${formatiere(Number(context.parsed))}`
                        }
                    }
                },
                responsive: true,
                maintainAspectRatio: false
            }
        };
    }

    /** Verlauf über Monate als Linie oder Balken (DEVCLR-327/364) - eine Reihe, Einheit an der y-Achse. */
    private configVerlauf(canvas: JQuery<HTMLElement>, daten: IChartData, typ: "line" | "bar", formatiere: (wert: number) => string): ChartConfiguration {
        return {
            type: typ,
            data: {
                labels: daten.labels,
                datasets: [{
                    label: canvas.data("karte-chart-reihe") as string,
                    data: daten.values,
                    backgroundColor: this.green5,
                    borderColor: this.green5
                }]
            },
            options: {
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (context) => formatiere(context.parsed.y ?? 0)
                        }
                    }
                },
                scales: {
                    y: {
                        // Ohne Werte setzt Chart.js die Achse auf -1..1 - ab 0 starten; negative Werte (Gutschriften) erweitern sie trotzdem.
                        beginAtZero: true,
                        ticks: {
                            // Zählwerte (z.B. Anzahl Aktionen, DEVCLR-371) ohne Zwischenschritte wie 0,2.
                            precision: daten.values.every(wert => Number.isInteger(wert)) ? 0 : undefined,
                            callback: (wert) => formatiere(Number(wert))
                        }
                    }
                },
                responsive: true,
                maintainAspectRatio: false
            }
        };
    }
}
